import { useEffect, useState } from 'react';
import api from '../services/api';
import useLang from '../hooks/useLang';

const SENSITIVE = ['whatsapp_token', 'sms_password', 'wasender_api_key', 'meta_whatsapp_token'];

export default function SystemSettings() {
  const { T } = useLang();
  const S = T.systemSettings;

  const [settings, setSettings] = useState({});
  const [hasValue, setHasValue] = useState({});
  const [editing, setEditing]   = useState({});
  const [draft, setDraft]       = useState({});
  const [saving, setSaving]     = useState(false);
  const [saveMsg, setSaveMsg]   = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [testResult, setTestResult] = useState({});
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  const [templates, setTemplates]     = useState([]);
  const [templateStatus, setTemplateStatus] = useState({}); // name -> {status, rejected_reason}
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [submitting, setSubmitting]   = useState(null);
  const [templateMsg, setTemplateMsg] = useState({});

  const loadTemplateCatalog = () => {
    api.get('/settings/whatsapp-template-catalog').then(({ data }) => setTemplates(data.templates || []));
  };

  const refreshTemplateStatus = () => {
    setTemplatesLoading(true);
    api.get('/settings/whatsapp-templates')
      .then(({ data }) => {
        if (!data.ok) { setTemplateMsg(m => ({ ...m, _global: data.error })); return; }
        const byName = {};
        (data.templates || []).forEach(t => { byName[t.name] = t; });
        setTemplateStatus(byName);
        setTemplateMsg(m => ({ ...m, _global: '' }));
      })
      .finally(() => setTemplatesLoading(false));
  };

  const submitTemplate = (key) => {
    setSubmitting(key);
    setTemplateMsg(m => ({ ...m, [key]: '' }));
    api.post('/settings/whatsapp-templates', { key })
      .then(({ data }) => {
        setTemplateMsg(m => ({ ...m, [key]: data.ok ? `✅ ${data.message}` : `❌ ${data.error}` }));
        if (data.ok) refreshTemplateStatus();
      })
      .catch(e => setTemplateMsg(m => ({ ...m, [key]: `❌ ${e.response?.data?.error || 'Imeshindwa'}` })))
      .finally(() => setSubmitting(null));
  };

  const deleteTemplate = (key) => {
    if (!confirm(`Futa template "${key}" kutoka Meta? Utaweza kuituma tena baada ya hapo.`)) return;
    setSubmitting(key);
    setTemplateMsg(m => ({ ...m, [key]: '' }));
    api.delete('/settings/whatsapp-templates', { data: { key } })
      .then(({ data }) => {
        setTemplateMsg(m => ({ ...m, [key]: data.ok ? `✅ ${data.message}` : `❌ ${data.error}` }));
        if (data.ok) {
          setTemplateStatus(s => { const n = { ...s }; delete n[key]; return n; });
          refreshTemplateStatus();
        }
      })
      .catch(e => setTemplateMsg(m => ({ ...m, [key]: `❌ ${e.response?.data?.error || 'Imeshindwa'}` })))
      .finally(() => setSubmitting(null));
  };

  useEffect(() => { loadTemplateCatalog(); }, []);

  useEffect(() => {
    api.get('/settings')
      .then(({ data }) => {
        const vals = {};
        const has  = {};
        (data.settings || []).forEach(s => {
          vals[s.key] = s.value;
          has[s.key]  = s.has_value;
        });
        setSettings(vals);
        setHasValue(has);
      })
      .catch(() => setError(S.loadFailed))
      .finally(() => setLoading(false));
  }, []);

  const provider = draft.whatsapp_provider ?? settings.whatsapp_provider ?? 'ultramsg';

  const startEdit = (key) => {
    setEditing(e => ({ ...e, [key]: true }));
    setDraft(d => ({ ...d, [key]: SENSITIVE.includes(key) ? '' : (settings[key] || '') }));
  };

  const cancelEdit = (key) => {
    setEditing(e => ({ ...e, [key]: false }));
    setDraft(d => { const n = { ...d }; delete n[key]; return n; });
  };

  const save = async () => {
    if (!Object.keys(draft).length) return;
    setSaving(true);
    setSaveMsg('');
    try {
      await api.put('/settings', draft);
      const { data } = await api.get('/settings');
      const vals = {};
      const has  = {};
      (data.settings || []).forEach(s => { vals[s.key] = s.value; has[s.key] = s.has_value; });
      setSettings(vals);
      setHasValue(has);
      setDraft({});
      setEditing({});
      setSaveMsg(S.saved);
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setSaveMsg(S.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  const runTest = async (type) => {
    if (!testPhone.trim()) return;
    setTestResult(r => ({ ...r, [type]: { loading: true } }));
    try {
      const endpoint = type === 'whatsapp' ? '/settings/test-whatsapp' : '/settings/test-sms';
      const { data } = await api.post(endpoint, { phone: testPhone.trim() });
      setTestResult(r => ({ ...r, [type]: { ok: data.ok, msg: data.message, loading: false } }));
    } catch (e) {
      setTestResult(r => ({ ...r, [type]: { ok: false, msg: e.response?.data?.error || 'Error', loading: false } }));
    }
  };

  const hasDraft = Object.keys(draft).length > 0;

  if (loading) return <div className="text-center py-20 text-gray-500">{T.common?.loading || '...'}</div>;
  if (error)   return <div className="text-center py-20 text-red-500">{error}</div>;

  const GROUPS = [
    {
      key: 'ultramsg',
      testKey: 'whatsapp',
      icon: '💬',
      color: 'green',
      title: S.whatsapp.title,
      hint: S.whatsapp.hint,
      link: { href: 'https://ultramsg.com', label: S.whatsapp.link },
      fields: S.whatsapp.fields,
    },
    {
      key: 'wasender',
      testKey: 'whatsapp',
      icon: '💬',
      color: 'green',
      title: S.wasender.title,
      hint: S.wasender.hint,
      link: { href: 'https://www.wasenderapi.com', label: S.wasender.link },
      fields: S.wasender.fields,
    },
    {
      key: 'meta',
      testKey: 'whatsapp',
      icon: '✅',
      color: 'green',
      title: S.meta.title,
      hint: S.meta.hint,
      link: { href: 'https://developers.facebook.com', label: S.meta.link },
      fields: S.meta.fields,
    },
    {
      key: 'sms',
      testKey: 'sms',
      icon: '📱',
      color: 'blue',
      title: S.sms.title,
      hint: S.sms.hint,
      fields: S.sms.fields,
    },
  ];

  // Only show the active WhatsApp provider credentials
  const visibleGroups = GROUPS.filter(g => {
    if (g.key === 'ultramsg') return provider === 'ultramsg';
    if (g.key === 'wasender') return provider === 'wasender';
    if (g.key === 'meta') return provider === 'meta';
    return true;
  });

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">⚙️ {S.title}</h1>
        <p className="text-sm text-gray-500 mt-1">{S.subtitle}</p>
      </div>

      {/* WhatsApp Provider Selector */}
      <div className="rounded-2xl border border-purple-200 bg-purple-50 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <span className="text-xl">📡</span>
          <h2 className="font-bold text-purple-800">{S.providerLabel}</h2>
        </div>
        <p className="text-xs text-purple-600">{S.providerHint}</p>
        <div className="flex gap-3">
          {['ultramsg', 'wasender', 'meta'].map(p => (
            <label key={p}
              className={`flex items-center gap-2 cursor-pointer rounded-xl border px-4 py-2.5 text-sm font-medium transition-all
                ${provider === p
                  ? 'border-purple-500 bg-white text-purple-800 shadow-sm'
                  : 'border-gray-200 bg-white/60 text-gray-500 hover:border-purple-300'}`}>
              <input
                type="radio"
                name="whatsapp_provider"
                value={p}
                checked={provider === p}
                onChange={() => setDraft(d => ({ ...d, whatsapp_provider: p }))}
                className="accent-purple-600"
              />
              {S.providers[p]}
            </label>
          ))}
        </div>
      </div>

      {visibleGroups.map(g => {
        const borderColor = g.color === 'green' ? 'border-green-200' : 'border-blue-200';
        const bgColor     = g.color === 'green' ? 'bg-green-50'      : 'bg-blue-50';
        const iconBg      = g.color === 'green' ? 'bg-green-100'     : 'bg-blue-100';
        const titleColor  = g.color === 'green' ? 'text-green-800'   : 'text-blue-800';
        const hintColor   = g.color === 'green' ? 'text-green-600'   : 'text-blue-600';
        const btnColor    = g.color === 'green'
          ? 'bg-green-600 hover:bg-green-700'
          : 'bg-blue-600 hover:bg-blue-700';

        return (
          <div key={g.key} className={`rounded-2xl border ${borderColor} ${bgColor} p-5 space-y-4`}>
            <div className="flex items-center gap-3">
              <div className={`flex h-11 w-11 items-center justify-center rounded-xl text-2xl ${iconBg}`}>
                {g.icon}
              </div>
              <div>
                <h2 className={`font-bold ${titleColor}`}>{g.title}</h2>
                {g.link && (
                  <a href={g.link.href} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-blue-500 hover:underline">{g.link.label}</a>
                )}
              </div>
            </div>

            <p className={`text-xs ${hintColor}`}>{g.hint}</p>

            {g.key === 'meta' && (
              <div className="bg-white rounded-xl border border-gray-200 p-3">
                <label className="text-xs font-semibold text-gray-600 block mb-1.5">{g.callbackLabel}</label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={`${window.location.origin}/api/whatsapp/webhook`}
                    onFocus={e => e.target.select()}
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-mono bg-gray-50 text-gray-700"
                  />
                  <button
                    type="button"
                    onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/api/whatsapp/webhook`)}
                    className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
                  >
                    📋
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {Object.keys(g.fields).map(key => {
                const isSens   = SENSITIVE.includes(key);
                const isActive = editing[key];
                const dValue   = draft[key];
                const sValue   = settings[key] || '';
                const has      = hasValue[key];

                return (
                  <div key={key} className="bg-white rounded-xl border border-gray-200 p-3">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <label className="text-xs font-semibold text-gray-600">{g.fields[key]}</label>
                      {!isActive && (
                        <button onClick={() => startEdit(key)}
                          className="text-xs text-blue-600 hover:text-blue-800 font-medium">
                          {has ? S.change : S.set}
                        </button>
                      )}
                    </div>

                    {isActive ? (
                      <div className="flex gap-2">
                        <input
                          type={isSens ? 'password' : 'text'}
                          value={dValue ?? ''}
                          onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))}
                          placeholder={isSens ? (has ? '••••••••' : S.set) : ''}
                          autoFocus
                          className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                        />
                        <button onClick={() => cancelEdit(key)}
                          className="text-xs text-gray-400 hover:text-gray-600 px-2">✕</button>
                      </div>
                    ) : (
                      <div className="text-sm font-mono text-gray-700">
                        {isSens
                          ? (has ? <span className="text-gray-400 tracking-widest">••••••••</span> : <span className="text-gray-300 italic">{S.notSet}</span>)
                          : (sValue || <span className="text-gray-300 italic">{S.notSet}</span>)
                        }
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Test section */}
            <div className="rounded-xl border border-dashed border-gray-300 bg-white/60 p-3">
              <p className="text-xs font-semibold text-gray-600 mb-2">{S.testTitle}</p>
              <div className="flex gap-2">
                <input
                  type="tel"
                  placeholder={S.testPhone}
                  value={testPhone}
                  onChange={e => setTestPhone(e.target.value)}
                  className="flex-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-400"
                />
                <button
                  onClick={() => runTest(g.testKey)}
                  disabled={!testPhone.trim() || testResult[g.testKey]?.loading}
                  className={`rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50 transition-colors ${btnColor}`}
                >
                  {testResult[g.testKey]?.loading ? S.testing : S.test}
                </button>
              </div>
              {testResult[g.testKey] && !testResult[g.testKey].loading && (
                <p className={`mt-2 text-xs font-medium ${testResult[g.testKey].ok ? 'text-green-600' : 'text-red-600'}`}>
                  {testResult[g.testKey].ok ? S.testOk : S.testFail} {testResult[g.testKey].msg}
                </p>
              )}
            </div>
          </div>
        );
      })}

      {/* WhatsApp Message Templates (Meta) */}
      {provider === 'meta' && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-bold text-emerald-800">📨 Templates za WhatsApp (Meta)</h2>
              <p className="text-xs text-emerald-600 mt-0.5">
                Zinafanana na ujumbe wa SMS uliopo — kila moja lazima itumwe na kuidhinishwa na Meta kabla ya kutumika.
              </p>
            </div>
            <button onClick={refreshTemplateStatus} disabled={templatesLoading}
              className="shrink-0 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50">
              {templatesLoading ? '...' : '🔄 Hali kutoka Meta'}
            </button>
          </div>

          {templateMsg._global && (
            <p className="text-xs text-red-600">{templateMsg._global}</p>
          )}

          <div className="space-y-3">
            {templates.map(t => {
              const live = templateStatus[t.key];
              const statusBadge = live ? (
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                  live.status === 'APPROVED' ? 'bg-green-100 text-green-700'
                  : live.status === 'REJECTED' ? 'bg-red-100 text-red-700'
                  : 'bg-amber-100 text-amber-700'
                }`}>{live.status}</span>
              ) : (
                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-500">Haijatumwa</span>
              );

              return (
                <div key={t.key} className="bg-white rounded-xl border border-gray-200 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-gray-800">{t.label}</p>
                        {statusBadge}
                      </div>
                      <p className="text-[11px] text-gray-400 mt-0.5">{t.source}</p>
                    </div>
                    <div className="flex shrink-0 gap-1.5">
                      {live && (
                        <button onClick={() => deleteTemplate(t.key)} disabled={submitting === t.key}
                          className="rounded-lg border border-red-300 text-red-600 px-3 py-1.5 text-xs font-semibold hover:bg-red-50 disabled:opacity-50">
                          Futa
                        </button>
                      )}
                      <button onClick={() => submitTemplate(t.key)} disabled={submitting === t.key}
                        className="rounded-lg bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                        {submitting === t.key ? '...' : (live ? 'Tuma Tena' : 'Tuma kwa Meta')}
                      </button>
                    </div>
                  </div>
                  {t.body && (
                    <pre className="mt-2 whitespace-pre-wrap rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600 font-mono">{t.body}</pre>
                  )}
                  {live?.rejected_reason && (
                    <p className="mt-1 text-xs text-red-600">Sababu ya kukataliwa: {live.rejected_reason}</p>
                  )}
                  {templateMsg[t.key] && (
                    <p className="mt-1 text-xs text-gray-600">{templateMsg[t.key]}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Save bar */}
      <div className={`sticky bottom-4 flex items-center gap-3 rounded-2xl border bg-white p-4 shadow-lg transition-all ${hasDraft ? 'border-green-300' : 'border-gray-200 opacity-60'}`}>
        <div className="flex-1">
          {saveMsg
            ? <span className="text-sm font-medium text-green-700">{saveMsg}</span>
            : <span className="text-sm text-gray-500">{hasDraft ? S.hasChanges : S.noChanges}</span>
          }
        </div>
        <button
          onClick={save}
          disabled={!hasDraft || saving}
          className="rounded-xl bg-gray-900 px-5 py-2 text-sm font-semibold text-white hover:bg-gray-700 disabled:opacity-40 transition-colors"
        >
          {saving ? S.saving : `💾 ${S.save}`}
        </button>
      </div>
    </div>
  );
}
